import { UserAgent, Registerer, RegistererState, Inviter, Invitation, SessionState } from 'sip.js';

// src/sip/SipService.ts

// src/constants/sip.ts
var SIP_DEFAULTS = {
  WSS_PORT: 8089,
  // 120s (was 300s): sip.js auto-refreshes at ~90% of expiry, so a device
  // that goes silent is evicted by Asterisk within ~2 min instead of 5,
  // shrinking the window where a call rings into a dead contact.
  REGISTER_EXPIRES: 120,
  KEEPALIVE_INTERVAL: 3e4,
  RECONNECT_MIN: 1e3,
  RECONNECT_MAX: 6e4,
  // Onvoorwaardelijke waakhond. De keepalive-controle helpt hier niet: die keert
  // meteen terug tenzij de state al `registered` is, dus juist in de toestand waar
  // de herverbinding vastligt kijkt niemand. Deze timer draait ongeacht de state.
  WATCHDOG_INTERVAL: 6e4,
  // Hoe lang een niet-geregistreerde client zonder geplande herverbinding mag
  // hangen voordat de waakhond hem forceert. Ruim boven RECONNECT_MAX (60s), zodat
  // een normale backoff nooit onderbroken wordt -- alleen een ketting die echt
  // stilstaat wordt aangeraakt.
  WATCHDOG_STUCK_MS: 15e4
};
var EXTENSION_RANGES = {
  VIDEOPHONE: { min: 7e3, max: 7999 },
  EMERGENCY: { min: 9e3, max: 9002 },
  ENTRANCE_DOOR: { min: 9100, max: 9199 }
};
var isEmergencyExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.EMERGENCY.min && num <= EXTENSION_RANGES.EMERGENCY.max;
};
var isVideophoneExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.VIDEOPHONE.min && num <= EXTENSION_RANGES.VIDEOPHONE.max;
};
var isEntranceDoorExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.ENTRANCE_DOOR.min && num <= EXTENSION_RANGES.ENTRANCE_DOOR.max;
};

// src/sip/SipService.ts
var CLEANUP_TIMEOUT_MS = 5e3;
var ICE_GATHERING_TIMEOUT_MS = 3e3;
var SipService = class _SipService {
  config = null;
  userAgent = null;
  registerer = null;
  state = "disconnected";
  handlers = /* @__PURE__ */ new Map();
  keepAliveTimer = null;
  reconnectTimer = null;
  reconnectDelay = SIP_DEFAULTS.RECONNECT_MIN;
  _cleaningUp = false;
  watchdogTimer = null;
  lastConnectAttemptAt = 0;
  async connect(config) {
    this.clearReconnectTimer();
    this._cleaningUp = false;
    this.config = config;
    this.lastConnectAttemptAt = Date.now();
    this.setState("connecting");
    this.startWatchdog();
    try {
      const transportOptions = {
        server: config.wssUrl,
        keepAliveInterval: SIP_DEFAULTS.KEEPALIVE_INTERVAL / 1e3
      };
      const uri = UserAgent.makeURI(`sip:${config.extension}@${config.realm}`);
      if (!uri) throw new Error(`Invalid SIP URI: sip:${config.extension}@${config.realm}`);
      const userAgentOptions = {
        uri,
        authorizationUsername: config.extension,
        authorizationPassword: config.password,
        displayName: config.displayName,
        transportOptions,
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: config.iceServers.map((s) => ({
              urls: s.urls,
              username: s.username,
              credential: s.credential
            }))
          }
        },
        logLevel: "warn"
      };
      this.userAgent = new UserAgent(userAgentOptions);
      this.userAgent.delegate = {
        onInvite: (invitation) => {
          this.emit("incomingCall", invitation);
        },
        // Transport (re)connected — make sure we're registered. On the initial
        // connect this fires before the registerer exists (no-op); on a later
        // spontaneous reconnect it re-asserts our registration as a safety net.
        onConnect: () => {
          console.log("[SipService] Transport connected");
          if (this.registerer && !this._cleaningUp) {
            this.registerer.register().catch((err) => {
              console.warn("[SipService] Re-register after connect failed:", err);
            });
          }
        },
        // Transport dropped (device sleep, Wi-Fi roam, network blip, server
        // restart). Without this handler the client keeps believing it's
        // "registered" while Asterisk evicts the contact after the expiry —
        // so an incoming call finds no route. Recover unless we tore down
        // intentionally.
        onDisconnect: (error) => {
          console.warn("[SipService] Transport disconnected:", error?.message ?? "(clean)");
          if (this._cleaningUp) return;
          this.setState("disconnected");
          this.emit("transportError", error ?? new Error("Transport disconnected"));
          this.scheduleReconnect();
        }
      };
      await this.userAgent.start();
      try {
        const transport = this.userAgent.transport;
        const ws = transport?._ws || transport?.ws;
        if (ws) {
          const origOnMessage = ws.onmessage;
          ws.onmessage = (event) => {
            if (typeof event.data === "string" && event.data.length > 4) {
              console.log("[SIP RAW \u2190]", event.data.substring(0, 500));
            }
            origOnMessage?.call(ws, event);
          };
          console.log("[SipService] Raw WS message interceptor installed");
        }
      } catch {
      }
      this.registerer = new Registerer(this.userAgent, {
        expires: SIP_DEFAULTS.REGISTER_EXPIRES
      });
      this.registerer.stateChange.addListener((newState) => {
        switch (newState) {
          case RegistererState.Registered:
            this.setState("registered");
            this.reconnectDelay = SIP_DEFAULTS.RECONNECT_MIN;
            break;
          case RegistererState.Unregistered:
            this.setState("disconnected");
            if (!this._cleaningUp) {
              console.warn("[SipService] Registration lost \u2014 scheduling reconnect");
              this.emit("registrationFailed", new Error("Registration rejected"));
              this.scheduleReconnect();
            }
            break;
          case RegistererState.Terminated:
            this.setState("disconnected");
            if (!this._cleaningUp) {
              console.warn("[SipService] Registerer terminated \u2014 scheduling reconnect");
              this.scheduleReconnect();
            }
            break;
        }
      });
      await this.registerer.register();
      this.startKeepAlive();
    } catch (err) {
      console.error("[SipService] Connection error:", err);
      this.setState("error");
      this.emit("transportError", err);
      this.scheduleReconnect();
    }
  }
  async disconnect() {
    this.stopKeepAlive();
    this.stopWatchdog();
    this.clearReconnectTimer();
    this._cleaningUp = true;
    if (this.registerer) {
      try {
        await this.registerer.unregister();
      } catch {
      }
      this.registerer = null;
    }
    if (this.userAgent) {
      try {
        await this.userAgent.stop();
      } catch {
      }
      this.userAgent = null;
    }
    this.config = null;
    this._cleaningUp = false;
    this.setState("disconnected");
  }
  /**
   * Create an outgoing call (Inviter).
   */
  makeCall(target) {
    if (!this.userAgent || this.state !== "registered") {
      console.warn("[SipService] Cannot make call \u2014 not registered");
      return null;
    }
    const targetUri = UserAgent.makeURI(`sip:${target}@${this.config.realm}`);
    if (!targetUri) {
      console.error(`[SipService] Invalid target URI: ${target}`);
      return null;
    }
    const inviter = new Inviter(this.userAgent, targetUri, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
        iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS
      }
    });
    return inviter;
  }
  /**
   * Answer an incoming call (Invitation).
   */
  async answerCall(invitation) {
    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
        iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS
      }
    });
  }
  /**
   * Reject an incoming call.
   */
  async rejectCall(invitation) {
    await invitation.reject();
  }
  isRegistered() {
    return this.state === "registered";
  }
  getState() {
    return this.state;
  }
  getConfig() {
    return this.config;
  }
  getUserAgent() {
    return this.userAgent;
  }
  /**
   * Vervang de ICE-servers voor de VOLGENDE gesprekken, zonder opnieuw te registreren.
   *
   * TURN-credentials verlopen (CARO-Api `TURN_CREDENTIAL_TTL`, standaard 24 u), maar
   * `connect()` bakte ze eenmalig in de UserAgent: een kiosktablet die langer dan een
   * dag draaide belde daarna zonder relay. sip.js leest
   * `userAgent.configuration.sessionDescriptionHandlerFactoryOptions` opnieuw bij elke
   * nieuwe sessie, dus dit object bijwerken volstaat. Een lopend gesprek houdt zijn
   * eigen PeerConnection en merkt hier niets van.
   */
  updateIceServers(iceServers) {
    if (this.config) this.config = { ...this.config, iceServers };
    const factoryOptions = this.userAgent?.configuration.sessionDescriptionHandlerFactoryOptions;
    if (!factoryOptions) return;
    factoryOptions.peerConnectionConfiguration = {
      ...factoryOptions.peerConnectionConfiguration,
      iceServers: iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential
      }))
    };
  }
  getKeepAliveInterval() {
    return SIP_DEFAULTS.KEEPALIVE_INTERVAL;
  }
  /**
   * Tear down UA/registerer without clearing config or reconnect state.
   * Used internally before reconnect attempts.
   */
  async cleanup() {
    this.stopKeepAlive();
    this._cleaningUp = true;
    if (this.registerer) {
      const r = this.registerer;
      this.registerer = null;
      try {
        await _SipService.withTimeout(
          Promise.resolve(r.unregister()),
          CLEANUP_TIMEOUT_MS,
          "registerer.unregister()"
        );
      } catch {
      }
    }
    if (this.userAgent) {
      const ua = this.userAgent;
      this.userAgent = null;
      try {
        await _SipService.withTimeout(
          Promise.resolve(ua.stop()),
          CLEANUP_TIMEOUT_MS,
          "userAgent.stop()"
        );
      } catch {
      }
    }
  }
  // --- Event system ---
  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, /* @__PURE__ */ new Set());
    }
    this.handlers.get(event).add(handler);
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }
  emit(event, data) {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        console.error(`[SipService] Event handler error (${event}):`, err);
      }
    });
  }
  // --- Internal ---
  setState(newState) {
    if (this.state !== newState) {
      this.state = newState;
      this.emit("stateChange", newState);
    }
  }
  startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      if (this.state !== "registered" || this._cleaningUp) return;
      const connected = this.userAgent?.transport?.isConnected() ?? false;
      if (!connected) {
        console.warn("[SipService] Keepalive: transport not connected \u2014 reconnecting");
        this.setState("disconnected");
        this.scheduleReconnect();
      }
    }, SIP_DEFAULTS.KEEPALIVE_INTERVAL);
  }
  stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }
  /**
   * Onvoorwaardelijke waakhond - het vangnet onder de herverbindingsketting.
   *
   * De keepalive-controle kan dit niet zijn: die begint met
   * `if (this.state !== 'registered') return`, dus zodra de client NIET geregistreerd
   * is kijkt er niemand meer. Dat is de verkeerde kant op - het moment waarop toezicht
   * nodig is, is precies het moment waarop die controle zichzelf uitschakelt.
   *
   * Deze timer draait ongeacht de state en herstelt de drie manieren waarop de ketting
   * definitief stil kon vallen:
   *   1. Een Registerer die `Terminated` werd. Die tak plande niets; de client bleef op
   *      `disconnected` staan zonder timer en zonder enig ander pad terug.
   *   2. Een `cleanup()` die bleef hangen op `userAgent.stop()` tegen een dode socket,
   *      waardoor `connect()` nooit werd bereikt en `_cleaningUp` op true bleef - wat
   *      elk volgend Unregistered/onDisconnect-event stil weggooit.
   *   3. Een `connect()` die blijft hangen in `userAgent.start()`.
   *
   * Hij raakt niets aan zolang er een herverbinding gepland staat, en wacht
   * WATCHDOG_STUCK_MS (ruim boven RECONNECT_MAX) zodat een normale backoff nooit
   * onderbroken wordt.
   */
  startWatchdog() {
    if (this.watchdogTimer) return;
    this.watchdogTimer = setInterval(() => {
      if (!this.config) return;
      if (this.state === "registered") return;
      if (this.reconnectTimer) return;
      const stuckFor = Date.now() - this.lastConnectAttemptAt;
      if (stuckFor < SIP_DEFAULTS.WATCHDOG_STUCK_MS) return;
      console.warn(
        `[SipService] Watchdog: ${this.state} voor ${Math.round(stuckFor / 1e3)}s zonder geplande herverbinding - ketting is stil gevallen, forceren.`
      );
      this._cleaningUp = false;
      this.reconnectDelay = SIP_DEFAULTS.RECONNECT_MIN;
      this.scheduleReconnect();
    }, SIP_DEFAULTS.WATCHDOG_INTERVAL);
    const t = this.watchdogTimer;
    if (typeof t.unref === "function") t.unref();
  }
  stopWatchdog() {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }
  /**
   * Wacht op `p`, maar geef na `ms` op. `userAgent.stop()` en `registerer.unregister()`
   * doen beide I/O over de WebSocket; tegen een socket die wel open lijkt maar niets
   * meer doorlaat kan die belofte nooit meer settelen. Zonder deze grens hangt de hele
   * herverbindingsketting daarachter - voor onbepaalde tijd.
   */
  static async withTimeout(p, ms, label) {
    let timer;
    try {
      await Promise.race([
        p,
        new Promise((resolve) => {
          timer = setTimeout(() => {
            console.warn(`[SipService] ${label} timed out after ${ms}ms - continuing anyway`);
            resolve(void 0);
          }, ms);
        })
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  scheduleReconnect() {
    this.clearReconnectTimer();
    const jitter = Math.random() * 1e3;
    const delay = Math.min(this.reconnectDelay + jitter, SIP_DEFAULTS.RECONNECT_MAX);
    console.log(`[SipService] Reconnecting in ${Math.round(delay)}ms`);
    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      const savedConfig = this.config;
      if (!savedConfig) return;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, SIP_DEFAULTS.RECONNECT_MAX);
      try {
        await this.cleanup();
        await this.connect(savedConfig);
      } catch (err) {
        console.error("[SipService] Reconnect attempt threw:", err);
        this._cleaningUp = false;
        this.scheduleReconnect();
      }
    }, delay);
  }
  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
};
function makeCallId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
  }
  return `call-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}
var CallManager = class {
  sipService;
  currentCall = null;
  currentSession = null;
  handlers = /* @__PURE__ */ new Map();
  constructor(sipService) {
    this.sipService = sipService;
  }
  /**
   * Initiate an outgoing call.
   */
  async initiate(target, type) {
    if (this.currentCall && this.currentCall.state !== "ended") {
      console.warn("[CallManager] Already in a call");
      return null;
    }
    const callType = type || this.inferCallType(target);
    const inviter = this.sipService.makeCall(target);
    if (!inviter) return null;
    const call = {
      id: makeCallId(),
      state: "dialing",
      type: callType,
      direction: "outbound",
      remoteParty: target,
      startedAt: (/* @__PURE__ */ new Date()).toISOString(),
      isEmergency: isEmergencyExtension(target),
      isAutoAnswer: false,
      isMuted: false,
      isSpeaker: false
    };
    this.currentCall = call;
    this.currentSession = inviter;
    this.emit("callStateChange", call);
    this.attachSessionListeners(inviter);
    try {
      await inviter.invite();
    } catch (err) {
      console.error("[CallManager] Invite failed:", err);
      this.updateCallState("ended");
      return null;
    }
    const traceId = this.extractTraceId(inviter);
    if (traceId && this.currentCall) {
      this.currentCall = { ...this.currentCall, traceId };
      this.emit("callStateChange", this.currentCall);
    }
    return this.currentCall;
  }
  /**
   * Handle an incoming call from SipService.
   */
  handleIncoming(invitation) {
    const remoteUri = invitation.remoteIdentity.uri.toString();
    const remoteExt = invitation.remoteIdentity.uri.user || remoteUri;
    const displayName = invitation.remoteIdentity.displayName || remoteExt;
    const call = {
      id: makeCallId(),
      state: "ringing_in",
      type: this.inferCallType(remoteExt),
      direction: "inbound",
      remoteParty: remoteExt,
      remoteDisplayName: displayName,
      startedAt: (/* @__PURE__ */ new Date()).toISOString(),
      isEmergency: isEmergencyExtension(remoteExt),
      isAutoAnswer: this.shouldAutoAnswer(
        remoteExt,
        this.extractSipHeaders(invitation)
      ),
      isMuted: false,
      isSpeaker: false,
      traceId: this.extractTraceId(invitation)
    };
    this.currentCall = call;
    this.currentSession = invitation;
    this.attachSessionListeners(invitation);
    this.emit("callStateChange", call);
    if (call.isAutoAnswer) {
      setTimeout(() => this.answer(), 500);
    }
    return call;
  }
  /**
   * Answer the current incoming call.
   */
  async answer() {
    if (!this.currentSession || !(this.currentSession instanceof Invitation)) {
      console.warn("[CallManager] No incoming call to answer");
      return;
    }
    await this.sipService.answerCall(this.currentSession);
  }
  /**
   * Reject the current incoming call.
   */
  async reject() {
    if (!this.currentSession || !(this.currentSession instanceof Invitation)) {
      console.warn("[CallManager] No incoming call to reject");
      return;
    }
    try {
      await this.sipService.rejectCall(this.currentSession);
      this.updateCallState("ended");
    } catch (err) {
      console.warn("[CallManager] Reject failed, falling back to hangup:", err);
      await this.hangup();
    }
  }
  /**
   * Put the current call on hold.
   */
  async hold() {
    if (!this.currentSession || this.currentCall?.state !== "active") return;
    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (sessionDescriptionHandler && "hold" in sessionDescriptionHandler) {
        await sessionDescriptionHandler.hold();
        this.updateCallState("held");
      }
    } catch (err) {
      console.error("[CallManager] Hold failed:", err);
    }
  }
  /**
   * Resume a held call.
   */
  async unhold() {
    if (!this.currentSession || this.currentCall?.state !== "held") return;
    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (sessionDescriptionHandler && "unhold" in sessionDescriptionHandler) {
        await sessionDescriptionHandler.unhold();
        this.updateCallState("active");
      }
    } catch (err) {
      console.error("[CallManager] Unhold failed:", err);
    }
  }
  /**
   * End the active call.
   */
  async hangup() {
    const session = this.currentSession;
    if (!session) return;
    const attempts = [];
    switch (session.state) {
      case SessionState.Initial:
      case SessionState.Establishing:
        if (session instanceof Inviter) {
          attempts.push(["cancel", () => session.cancel()]);
        } else if (session instanceof Invitation) {
          attempts.push(["reject", () => session.reject()]);
        }
        break;
      case SessionState.Established:
        attempts.push(["bye", () => session.bye()]);
        break;
    }
    if (session instanceof Invitation) {
      attempts.push(["reject(fallback)", () => session.reject()]);
    }
    if (session instanceof Inviter) {
      attempts.push(["cancel(fallback)", () => session.cancel()]);
    }
    attempts.push(["bye(fallback)", () => session.bye()]);
    let terminated = session.state === SessionState.Terminated;
    let lastError = null;
    for (const [label, run] of attempts) {
      if (terminated || session.state === SessionState.Terminated) {
        terminated = true;
        break;
      }
      try {
        await run();
        terminated = true;
        break;
      } catch (err) {
        lastError = err;
        console.warn(`[CallManager] hangup via ${label} failed:`, err);
      }
    }
    if (!terminated) {
      try {
        await session.dispose();
      } catch (err) {
        lastError = err;
      }
      console.error("[CallManager] Hangup could not terminate the session", lastError);
      this.emit("hangupFailed", { state: session.state, error: lastError });
    }
    this.updateCallState("ended");
  }
  /**
   * Send DTMF tones (e.g. for door open codes).
   * Uses RFC 4733 (RTCDTMFSender) for WebRTC, falls back to SIP INFO.
   */
  async sendDtmf(digits) {
    if (!this.currentSession || this.currentCall?.state !== "active") {
      console.warn("[CallManager] Cannot send DTMF \u2014 no active call");
      return;
    }
    try {
      const pc = this.getPeerConnection();
      const audioSender = pc?.getSenders().find((s) => s.track?.kind === "audio");
      if (audioSender?.dtmf) {
        console.log("[CallManager] Sending DTMF via RFC 4733:", digits);
        audioSender.dtmf.insertDTMF(digits, 160, 80);
        this.emit("dtmfSent", digits);
        return;
      }
      console.log("[CallManager] Sending DTMF via SIP INFO:", digits);
      for (const digit of digits) {
        const body = {
          contentDisposition: "render",
          contentType: "application/dtmf-relay",
          content: `Signal=${digit}\r
Duration=160`
        };
        await this.currentSession.info({ requestOptions: { body } });
        await new Promise((r) => setTimeout(r, 200));
      }
      this.emit("dtmfSent", digits);
    } catch (err) {
      console.error("[CallManager] DTMF failed:", err);
    }
  }
  /**
   * Toggle mute on the current call.
   */
  toggleMute() {
    if (!this.currentCall || !this.currentSession) return;
    const pc = this.getPeerConnection();
    if (!pc) return;
    const audioSenders = pc.getSenders().filter((s) => s.track?.kind === "audio");
    const muted = !this.currentCall.isMuted;
    audioSenders.forEach((sender) => {
      if (sender.track) sender.track.enabled = !muted;
    });
    this.currentCall = { ...this.currentCall, isMuted: muted };
    this.emit("callStateChange", this.currentCall);
  }
  /**
   * Toggle speaker (for native bridge integration).
   */
  toggleSpeaker() {
    if (!this.currentCall) return;
    this.currentCall = { ...this.currentCall, isSpeaker: !this.currentCall.isSpeaker };
    this.emit("callStateChange", this.currentCall);
  }
  getCurrentCall() {
    return this.currentCall;
  }
  /**
   * Get the underlying RTCPeerConnection (for stats/media access).
   */
  getPeerConnection() {
    if (!this.currentSession) return null;
    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!sdh) return null;
    try {
      if (sdh.peerConnection) return sdh.peerConnection;
    } catch {
    }
    return sdh._peerConnection || null;
  }
  /**
   * Get the remote MediaStream managed by sip.js's SessionDescriptionHandler.
   * This is the most reliable way to get remote audio.
   */
  getRemoteMediaStream() {
    if (!this.currentSession) return null;
    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!sdh) return null;
    return sdh.remoteMediaStream || sdh._remoteMediaStream || null;
  }
  /**
   * Determine if an incoming call should be auto-answered.
   */
  shouldAutoAnswer(callerExtension, headers) {
    if (isEmergencyExtension(callerExtension)) return true;
    if (headers?.["Alert-Info"]?.includes("auto-answer")) return true;
    return false;
  }
  // --- Event system ---
  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, /* @__PURE__ */ new Set());
    }
    this.handlers.get(event).add(handler);
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }
  emit(event, data) {
    this.handlers.get(event)?.forEach((h) => {
      try {
        h(data);
      } catch (err) {
        console.error(`[CallManager] Event handler error (${event}):`, err);
      }
    });
  }
  // --- Internal ---
  attachSessionListeners(session) {
    session.stateChange.addListener((newState) => {
      switch (newState) {
        case SessionState.Establishing:
          this.updateCallState("connecting");
          break;
        case SessionState.Established:
          if (this.currentCall) {
            this.currentCall = {
              ...this.currentCall,
              connectedAt: (/* @__PURE__ */ new Date()).toISOString()
            };
          }
          this.updateCallState("active");
          break;
        case SessionState.Terminated:
          this.updateCallState("ended");
          break;
      }
    });
  }
  updateCallState(state) {
    if (!this.currentCall) return;
    const updatedCall = {
      ...this.currentCall,
      state
    };
    if (state === "ended") {
      updatedCall.endedAt = (/* @__PURE__ */ new Date()).toISOString();
      if (updatedCall.connectedAt) {
        updatedCall.duration = Math.round(
          (new Date(updatedCall.endedAt).getTime() - new Date(updatedCall.connectedAt).getTime()) / 1e3
        );
      }
    }
    this.currentCall = updatedCall;
    this.emit("callStateChange", updatedCall);
    if (state === "ended") {
      this.emit("callEnded", updatedCall);
      setTimeout(() => {
        this.currentCall = null;
        this.currentSession = null;
      }, 2e3);
    }
  }
  inferCallType(extension) {
    if (isEmergencyExtension(extension)) return "emergency";
    if (isVideophoneExtension(extension)) return "doorbell";
    if (isEntranceDoorExtension(extension)) return "intercom";
    return "voice";
  }
  /**
   * Resolve the call-trace correlation id for a session. Prefers the
   * X-Caro-Call-Id header (Asterisk injects the channel linkedid there so all
   * legs share one key); otherwise uses the SIP Call-ID of this leg. Works for
   * both an inbound Invitation and an outbound Inviter (both expose `.request`
   * with `.callId`; only inbound requests carry the header).
   */
  extractTraceId(session) {
    try {
      const req = session.request;
      if (req && typeof req.getHeader === "function") {
        const injected = req.getHeader("X-Caro-Call-Id");
        if (injected) return injected;
      }
      return req?.callId || void 0;
    } catch {
      return void 0;
    }
  }
  extractSipHeaders(invitation) {
    const headers = {};
    try {
      const msg = invitation.request;
      const alertInfo = msg.getHeader("Alert-Info");
      if (alertInfo) headers["Alert-Info"] = alertInfo;
      const callInfo = msg.getHeader("Call-Info");
      if (callInfo) headers["Call-Info"] = callInfo;
    } catch {
    }
    return headers;
  }
};

// src/sip/CallTones.ts
var sharedContext = null;
var keepAliveStarted = false;
function getSharedContext() {
  if (!sharedContext) {
    sharedContext = new AudioContext();
  }
  if (sharedContext.state === "suspended") {
    sharedContext.resume().catch(() => {
    });
  }
  return sharedContext;
}
function getUnlockedAudioContext() {
  return getSharedContext();
}
async function suspendSharedAudioContext() {
  if (!sharedContext || sharedContext.state !== "running") return false;
  try {
    await sharedContext.suspend();
    return true;
  } catch {
    return false;
  }
}
async function resumeSharedAudioContext() {
  if (!sharedContext || sharedContext.state !== "suspended") return false;
  try {
    await sharedContext.resume();
    return true;
  } catch {
    return false;
  }
}
async function ensureRunning(ctx) {
  if (ctx.state === "running") return true;
  try {
    await ctx.resume();
  } catch {
    return false;
  }
  return ctx.state === "running";
}
function startKeepAlive(ctx) {
  if (keepAliveStarted) return;
  try {
    const silent = ctx.createGain();
    silent.gain.value = 0;
    silent.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.frequency.value = 30;
    osc.connect(silent);
    osc.start();
    keepAliveStarted = true;
  } catch {
  }
}
if (typeof document !== "undefined") {
  const unlock = () => {
    if (!sharedContext) {
      sharedContext = new AudioContext();
    }
    const ctx = sharedContext;
    if (ctx.state === "suspended") {
      ctx.resume().then(() => startKeepAlive(ctx)).catch(() => {
      });
    } else {
      startKeepAlive(ctx);
    }
  };
  document.addEventListener("click", unlock, { capture: true, passive: true });
  document.addEventListener("touchstart", unlock, { capture: true, passive: true });
  document.addEventListener("keydown", unlock, { capture: true, passive: true });
}
var CallTones = class {
  intervalId = null;
  activeNodes = [];
  // Bumped by stop(). An async start that resolves after a stop must not
  // begin playing — otherwise a ringtone outlives the call that triggered it.
  generation = 0;
  /**
   * Play a ringback tone (what the caller hears while waiting).
   * Standard North American ringback: 440Hz + 480Hz, 2s on / 4s off
   */
  async startRingback() {
    this.stop();
    const mine = this.generation;
    const ctx = getSharedContext();
    const running = await ensureRunning(ctx);
    if (this.generation !== mine) return "cancelled";
    if (!running) return "blocked";
    const playBurst = () => {
      try {
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 2);
        gain.connect(ctx.destination);
        const osc1 = ctx.createOscillator();
        osc1.frequency.value = 440;
        osc1.type = "sine";
        osc1.connect(gain);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 2);
        const osc2 = ctx.createOscillator();
        osc2.frequency.value = 480;
        osc2.type = "sine";
        osc2.connect(gain);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 2);
        this.activeNodes.push(osc1, osc2, gain);
      } catch {
      }
    };
    playBurst();
    this.intervalId = setInterval(playBurst, 6e3);
    return "playing";
  }
  /**
   * Play a ringtone (what the callee hears on incoming call).
   * European-style double-ring: two short bursts per cycle.
   */
  async startRingtone() {
    this.stop();
    const mine = this.generation;
    const ctx = getSharedContext();
    const running = await ensureRunning(ctx);
    if (this.generation !== mine) return "cancelled";
    if (!running) return "blocked";
    startKeepAlive(ctx);
    const playRing = () => {
      try {
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        const LEVEL = 0.85;
        gain.gain.setValueAtTime(LEVEL, ctx.currentTime);
        gain.gain.setValueAtTime(0, ctx.currentTime + 0.4);
        gain.gain.setValueAtTime(LEVEL, ctx.currentTime + 0.6);
        gain.gain.setValueAtTime(0, ctx.currentTime + 1);
        [440, 554].forEach((freq) => {
          const osc = ctx.createOscillator();
          osc.frequency.value = freq;
          osc.type = "sine";
          const oscGain = ctx.createGain();
          oscGain.gain.value = 0.5;
          osc.connect(oscGain);
          oscGain.connect(gain);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 1);
          this.activeNodes.push(osc, oscGain);
        });
        this.activeNodes.push(gain);
      } catch {
      }
    };
    playRing();
    this.intervalId = setInterval(playRing, 3e3);
    return "playing";
  }
  /**
   * Stop all tones.
   */
  stop() {
    this.generation += 1;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.activeNodes.forEach((node) => {
      try {
        if (node instanceof OscillatorNode) node.stop();
        node.disconnect();
      } catch {
      }
    });
    this.activeNodes = [];
  }
};

// src/sip/WebRTCMedia.ts
var MIC_QUIET_ENERGY_PER_SECOND = 0.01;
var MIC_SILENT_ENERGY = 1e-6;
var NO_TRANSPORT_EXPLANATION = "ICE/DTLS kwam niet tot stand: er is geen geslaagd kandidatenpaar tussen tablet en Asterisk. Zonder transport vertrekt en komt er geen RTP, in geen van beide richtingen -- dat is een gevolg, geen storing van de microfoon, de encoder of het afspelen. Kijk naar TURN (verlopen credentials, coturn), de ICE-kandidaten en de DTLS-handshake.";
function transportDown(report) {
  if (report.iceState === "failed" || report.dtlsState === "failed") return true;
  if (report.statsError) return false;
  if (report.candidatePair !== void 0) return false;
  return report.iceState !== void 0 || report.dtlsState !== void 0;
}
var WebRTCMedia = class {
  localStream = null;
  remoteAudioElement = null;
  outputMode = "element";
  // Bumped door warmUp() en playRemoteStream(). Zorgt dat een asynchrone opwarming
  // nooit een stream aanraakt die na haar start gekoppeld is. Zie warmUp().
  playbackGeneration = 0;
  webAudioCtx = null;
  webAudioSource = null;
  webAudioGain = null;
  ownsWebAudioCtx = false;
  /**
   * Get local audio stream for calls.
   */
  async getLocalAudioStream() {
    if (this.localStream) return this.localStream;
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: false
    });
    return this.localStream;
  }
  /**
   * Pre-create the audio element during a user gesture (tap Call / Answer).
   * This "warms up" the audio context so play() succeeds later when remote
   * media arrives, even if the actual play call is in a state-change callback.
   */
  warmUp() {
    const el = this.ensureAudioElement();
    if (el.srcObject) {
      console.log("[WebRTCMedia] Warm-up skipped \u2014 a live stream is already attached");
      return;
    }
    el.srcObject = null;
    el.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
    el.volume = 1;
    const mine = ++this.playbackGeneration;
    el.play().then(() => {
      if (this.playbackGeneration !== mine) {
        console.log("[WebRTCMedia] Warm-up finished after a stream was attached \u2014 leaving it alone");
        return;
      }
      el.pause();
      el.src = "";
      console.log("[WebRTCMedia] Audio element warmed up (autoplay unlocked)");
    }).catch(() => {
      console.log("[WebRTCMedia] Warm-up play ignored (will retry on stream attach)");
    });
  }
  ensureAudioElement() {
    if (!this.remoteAudioElement) {
      this.remoteAudioElement = document.createElement("audio");
      this.remoteAudioElement.autoplay = true;
      this.remoteAudioElement.volume = 1;
      this.remoteAudioElement.setAttribute("playsinline", "");
      this.remoteAudioElement.style.position = "fixed";
      this.remoteAudioElement.style.top = "-1px";
      this.remoteAudioElement.style.left = "-1px";
      this.remoteAudioElement.style.width = "1px";
      this.remoteAudioElement.style.height = "1px";
      this.remoteAudioElement.style.opacity = "0";
      this.remoteAudioElement.style.pointerEvents = "none";
      document.body.appendChild(this.remoteAudioElement);
    }
    return this.remoteAudioElement;
  }
  /**
   * Kies langs welke weg de gespreksaudio wordt afgespeeld.
   *
   *   'element'  Het <audio>-element met srcObject. De huidige, standaard weg.
   *   'webaudio' De stream door een AudioContext-graaf. Een ANDERE renderweg in
   *              Chromium -- dezelfde die de beltoon gebruikt, en die op toestellen
   *              waar de gespreksaudio stil blijft aantoonbaar wel hoorbaar is.
   *
   * Bestaat als schakelaar en niet als vervanging omdat het per toestelmodel verschilt
   * welke weg werkt, en omdat dit op een vloot van honderden tablets uitgeprobeerd moet
   * kunnen worden zonder voor iedereen tegelijk te veranderen.
   */
  setOutputMode(mode) {
    this.outputMode = mode;
  }
  getOutputMode() {
    return this.outputMode;
  }
  /** Speel de stream af via een AudioContext in plaats van via het <audio>-element. */
  playViaWebAudio(stream) {
    try {
      this.teardownWebAudio();
      const ctx = getUnlockedAudioContext();
      this.webAudioCtx = ctx;
      this.ownsWebAudioCtx = false;
      if (ctx.state === "suspended") void ctx.resume();
      const el = this.ensureAudioElement();
      this.playbackGeneration += 1;
      el.srcObject = stream;
      el.muted = true;
      el.play().catch((err) => console.warn("[WebRTCMedia] Muted pump-element play() blocked:", err.message));
      const src = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = 1;
      src.connect(gain);
      gain.connect(ctx.destination);
      this.webAudioSource = src;
      this.webAudioGain = gain;
      console.log("[WebRTCMedia] Remote stream via Web Audio (+ muted pump element), ctx state:", ctx.state);
    } catch (err) {
      console.error("[WebRTCMedia] Web Audio path failed, falling back to element:", err);
      this.outputMode = "element";
      this.playRemoteStream(stream);
    }
  }
  teardownWebAudio() {
    try {
      this.webAudioSource?.disconnect();
    } catch {
    }
    this.webAudioSource = null;
    try {
      this.webAudioGain?.disconnect();
    } catch {
    }
    this.webAudioGain = null;
    if (this.ownsWebAudioCtx && this.webAudioCtx && this.webAudioCtx.state !== "closed") {
      void this.webAudioCtx.close();
    }
    this.webAudioCtx = null;
  }
  /**
   * Play a remote MediaStream (preferred — use sip.js's remoteMediaStream).
   */
  playRemoteStream(stream) {
    if (this.outputMode === "webaudio") {
      this.playViaWebAudio(stream);
      return;
    }
    const el = this.ensureAudioElement();
    this.playbackGeneration += 1;
    el.srcObject = stream;
    el.muted = false;
    el.volume = 1;
    const attemptPlay = (attempt) => {
      el.play().then(() => {
        console.log(`[WebRTCMedia] play() succeeded (attempt ${attempt})`);
      }).catch((err) => {
        console.warn(`[WebRTCMedia] play() blocked (attempt ${attempt}):`, err.message);
        if (attempt < 5) {
          setTimeout(() => attemptPlay(attempt + 1), 500);
        }
      });
    };
    attemptPlay(1);
    console.log(
      "[WebRTCMedia] Playing remote stream, tracks:",
      stream.getAudioTracks().map((t) => `${t.kind}:${t.id}(${t.readyState})`).join(", ") || "none"
    );
  }
  /**
   * Attach remote audio from a PeerConnection (fallback if remoteMediaStream unavailable).
   */
  attachRemoteAudio(pc) {
    const el = this.ensureAudioElement();
    this.playbackGeneration += 1;
    const remoteStream = new MediaStream();
    const deliver = () => {
      if (this.outputMode === "webaudio") {
        this.playViaWebAudio(remoteStream);
        return true;
      }
      return false;
    };
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });
    pc.addEventListener("track", (event) => {
      console.log("[WebRTCMedia] ontrack:", event.track.kind, event.track.id);
      const existing = remoteStream.getTracks().find((t) => t.id === event.track.id);
      if (!existing) {
        remoteStream.addTrack(event.track);
      }
      if (deliver()) return;
      el.srcObject = remoteStream;
      el.volume = 1;
      el.play().catch(
        (err) => console.warn("[WebRTCMedia] play() blocked:", err.message)
      );
    });
    if (!deliver()) {
      el.srcObject = remoteStream;
      el.volume = 1;
      el.play().catch(
        (err) => console.warn("[WebRTCMedia] Initial play() blocked:", err.message)
      );
    }
    console.log(
      "[WebRTCMedia] Attached via PeerConnection, receivers:",
      pc.getReceivers().length,
      "tracks:",
      remoteStream.getTracks().length
    );
  }
  /**
   * Stop all local media tracks.
   */
  stopLocalStream() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
  }
  /**
   * Clean up all media.
   */
  cleanup() {
    this.stopLocalStream();
    this.teardownWebAudio();
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      this.remoteAudioElement.remove();
      this.remoteAudioElement = null;
    }
  }
  /**
   * Eén momentopname van de INKOMENDE audio, plus de toestand van het element dat hem
   * hoort af te spelen.
   *
   * Waarom dit er moet zijn: `device.media_connected` vuurt op ICE/DTLS, en dat is de
   * verkeerde vraag. Een geslaagde ICE-verbinding bewijst dat er een pad IS, niet dat er
   * pakketten OVERHEEN kwamen, en zeker niet dat ze naar geluid decodeerden. Bij
   * eenrichtingsaudio staat alles groen en hoort de bewoner niets — en tot nu toe was er
   * geen enkel cijfer om te zeggen welke schakel het liet vallen.
   *
   * De drie getallen die het uit elkaar trekken:
   *   packetsReceived    0 => het netwerk. RTP komt niet aan (SRTP/DTLS, NAT, firewall).
   *   totalAudioEnergy   0 terwijl pakketten binnenkomen => de tegenpartij stuurt stilte.
   *   element paused/    beide > 0 maar het element speelt niet => de webkant.
   *   muted/volume
   *
   * Zijn alle drie in orde en hoort de bewoner nog steeds niets, dan ligt het ONDER de
   * WebView — Android-routering of het volume van de gespreksstream. Dat is een conclusie
   * die je alleen kunt trekken als je deze cijfers hebt.
   */
  async getInboundAudioReport(pc) {
    const report = {
      at: Date.now(),
      packetsReceived: 0,
      bytesReceived: 0,
      packetsLost: 0,
      totalAudioEnergy: 0,
      totalSamplesDuration: 0,
      concealedSamples: 0,
      iceState: pc.iceConnectionState,
      connectionState: pc.connectionState
    };
    try {
      const stats = await pc.getStats();
      stats.forEach((stat) => {
        if (stat.type === "inbound-rtp" && (stat.kind === "audio" || stat.mediaType === "audio")) {
          report.packetsReceived = stat.packetsReceived ?? 0;
          report.bytesReceived = stat.bytesReceived ?? 0;
          report.packetsLost = stat.packetsLost ?? 0;
          report.jitterMs = typeof stat.jitter === "number" ? stat.jitter * 1e3 : void 0;
          if (typeof stat.totalAudioEnergy === "number") report.totalAudioEnergy = stat.totalAudioEnergy;
          if (typeof stat.totalSamplesDuration === "number") report.totalSamplesDuration = stat.totalSamplesDuration;
          if (typeof stat.concealedSamples === "number") report.concealedSamples = stat.concealedSamples;
          if (typeof stat.audioLevel === "number") report.audioLevel = stat.audioLevel;
        }
        if (stat.type === "track" && stat.kind === "audio" && stat.remoteSource) {
          if (report.totalAudioEnergy === 0 && typeof stat.totalAudioEnergy === "number") {
            report.totalAudioEnergy = stat.totalAudioEnergy;
          }
          if (report.audioLevel === void 0 && typeof stat.audioLevel === "number") {
            report.audioLevel = stat.audioLevel;
          }
        }
        if (stat.type === "codec" && typeof stat.mimeType === "string" && stat.mimeType.startsWith("audio/")) {
          report.codec = stat.mimeType;
        }
        if (stat.type === "outbound-rtp" && (stat.kind === "audio" || stat.mediaType === "audio")) {
          report.packetsSent = stat.packetsSent ?? 0;
          report.bytesSent = stat.bytesSent ?? 0;
        }
        if (stat.type === "media-source" && stat.kind === "audio") {
          if (typeof stat.totalAudioEnergy === "number") report.micEnergy = stat.totalAudioEnergy;
          if (typeof stat.audioLevel === "number") report.micLevel = stat.audioLevel;
        }
        if (stat.type === "media-playout") {
          if (typeof stat.totalSamplesCount === "number") report.playoutSamples = stat.totalSamplesCount;
          if (typeof stat.synthesizedSamplesDuration === "number") {
            report.synthesizedSamplesDuration = stat.synthesizedSamplesDuration;
          }
        }
        if (stat.type === "candidate-pair" && stat.state === "succeeded" && stat.nominated !== false) {
          report.rttMs = typeof stat.currentRoundTripTime === "number" ? stat.currentRoundTripTime * 1e3 : void 0;
          const local = stats.get(stat.localCandidateId);
          const remote = stats.get(stat.remoteCandidateId);
          report.candidatePair = `${local?.candidateType || "?"}->${remote?.candidateType || "?"}`;
        }
        if (stat.type === "transport" && typeof stat.dtlsState === "string") {
          report.dtlsState = stat.dtlsState;
        }
      });
    } catch (err) {
      report.statsError = err instanceof Error ? err.message : String(err);
    }
    try {
      const micTrack = pc.getSenders().map((s) => s.track).find((t) => t && t.kind === "audio");
      if (micTrack) {
        report.micTrack = {
          enabled: micTrack.enabled,
          muted: micTrack.muted,
          readyState: micTrack.readyState
        };
      }
    } catch {
    }
    report.outputPath = this.outputMode;
    if (this.outputMode === "webaudio") {
      report.webAudio = {
        contextState: this.webAudioCtx ? this.webAudioCtx.state : "none",
        sourceAttached: Boolean(this.webAudioSource)
      };
    }
    const el = this.remoteAudioElement;
    if (el) {
      report.element = {
        paused: el.paused,
        muted: el.muted,
        volume: el.volume,
        readyState: el.readyState,
        hasStream: Boolean(el.srcObject),
        trackCount: el.srcObject instanceof MediaStream ? el.srcObject.getAudioTracks().length : 0,
        // setSinkId bestaat niet in de Android WebView; undefined is hier normaal en
        // betekent "standaarduitgang", niet "kapot".
        sinkId: el.sinkId || void 0
      };
    }
    return report;
  }
  /**
   * Vergelijk twee momentopnames en trek de conclusie.
   *
   * Bewust op DELTA's en niet op absolute waarden: een teller die op 400 staat zegt niets
   * als hij daar al tien seconden op staat. Alleen groei bewijst dat er nu iets stroomt.
   */
  static diagnoseInboundAudio(first, second) {
    const dPackets = second.packetsReceived - first.packetsReceived;
    const dBytes = second.bytesReceived - first.bytesReceived;
    const dEnergy = second.totalAudioEnergy - first.totalAudioEnergy;
    const el = second.element;
    if (dPackets <= 0 && dBytes <= 0 && transportDown(second)) {
      return { ok: false, reason: "no_transport", explanation: NO_TRANSPORT_EXPLANATION };
    }
    if (dPackets <= 0 && dBytes <= 0) {
      return {
        ok: false,
        reason: "no_rtp",
        // Het pad staat (er is een geslaagd kandidatenpaar), maar er komt niets doorheen.
        explanation: "Geen inkomende RTP. Het probleem zit tussen Asterisk en de tablet (SRTP/DTLS, NAT of firewall), niet in de app."
      };
    }
    if (second.outputPath === "webaudio") {
      const wa = second.webAudio;
      if (!wa || wa.contextState !== "running" || !wa.sourceAttached) {
        return {
          ok: false,
          reason: "not_playing",
          explanation: `De Web Audio-weg staat niet aan het werk (context: ${wa ? wa.contextState : "ontbreekt"}, bron gekoppeld: ${wa ? String(wa.sourceAttached) : "nee"}). Zolang de graaf niet draait blijft de energie nul -- dat is een gevolg, geen aparte storing.`
        };
      }
    } else {
      if (el && (el.paused || el.muted || el.volume === 0 || !el.hasStream)) {
        return {
          ok: false,
          reason: "not_playing",
          explanation: "Het <audio>-element speelt niet (gepauzeerd, gedempt, volume 0 of geen stream). Zolang niets de track consumeert blijft de energie nul -- dat is een gevolg, geen aparte storing. Dit is de webkant."
        };
      }
      if (typeof first.playoutSamples === "number" && typeof second.playoutSamples === "number") {
        const dPlayout = second.playoutSamples - first.playoutSamples;
        if (dEnergy > 0 && dPlayout <= 0) {
          return {
            ok: false,
            reason: "not_playing",
            explanation: "Audio decodeert wel, maar het audio-apparaat haalt geen samples op (media-playout staat stil). De stroom komt binnen en wordt door niets afgespeeld -- dit is de uitgang, niet het netwerk."
          };
        }
      }
    }
    if (dEnergy <= 0) {
      return {
        ok: false,
        reason: "decoded_silence",
        explanation: "RTP komt aan maar decodeert naar stilte, terwijl het element wel speelt. De tegenpartij stuurt geen geluid, of het wordt onderweg vervangen door comfort noise."
      };
    }
    return {
      ok: true,
      reason: "receiving",
      // De belangrijkste zin van dit hele mechanisme. Als de bewoner NIETS hoort terwijl
      // dit verdict staat, is elke schakel tot en met de WebView aantoonbaar in orde en
      // ligt het eronder: Android-audioroutering of het volume van de gespreksstream
      // (apart van het mediavolume, en alleen tijdens een gesprek regelbaar).
      explanation: "Inkomende audio komt aan, decodeert naar echt geluid en het element speelt. Hoort de bewoner t\xF3ch niets, dan ligt het onder de WebView: Android-audioroutering of het volume van de gespreksstream."
    };
  }
  /**
   * De richting tablet -> Asterisk, uit dezelfde twee momentopnames.
   *
   * Dit is ALLEEN een meting. Er hangt geen reparatie aan en dat is een besluit, geen
   * gemis: op 2026-09-01 verving een waakhond midden in een goed gesprek de microfoon
   * op grond van precies dit soort energiecijfers, en maakte dat gesprek daarmee
   * eenrichtings. Energie zegt of er signaal is; wat je eraan doet is aan een mens.
   *
   * Drie uitkomsten die iets bewijzen, en één die dat eerlijk niet doet:
   *   no_outbound_rtp  er vertrekt niets. De encoder loopt niet of de sender is weg.
   *   mic_silent       pakketten vertrekken, de bron levert DIGITALE stilte (energie
   *                    exact nul). Een live microfoon doet dat nooit -- ook een stille
   *                    kamer geeft ruis. Dit is een uitgeschakelde of gemute bron.
   *   sending          er vertrekt RTP met signaal erin. `micQuiet` staat erbij als het
   *                    signaal ver onder spraakniveau zit; dat kan de bewoner zijn die
   *                    niets zei, dus het is geen oordeel maar een vlag.
   *   unknown          deze WebView levert geen outbound-tellers. Dan weten we het niet
   *                    en zeggen we dat, in plaats van groen te kleuren.
   */
  static diagnoseOutboundAudio(first, second) {
    const haveRtp = typeof first.packetsSent === "number" && typeof second.packetsSent === "number";
    const haveMic = typeof first.micEnergy === "number" && typeof second.micEnergy === "number";
    const packetsDelta = haveRtp ? second.packetsSent - first.packetsSent : null;
    const micEnergyDelta = haveMic ? second.micEnergy - first.micEnergy : null;
    const windowS = Math.max((second.at - first.at) / 1e3, 1e-3);
    const micQuiet = micEnergyDelta !== null && micEnergyDelta > MIC_SILENT_ENERGY && micEnergyDelta < MIC_QUIET_ENERGY_PER_SECOND * windowS;
    if (!haveRtp) {
      return {
        ok: false,
        reason: "unknown",
        explanation: "Deze WebView levert geen outbound-rtp-statistiek; over de uitgaande richting is hier niets te zeggen.",
        packetsDelta,
        micEnergyDelta,
        micQuiet: false
      };
    }
    if (packetsDelta <= 0 && transportDown(second)) {
      return {
        ok: false,
        reason: "no_transport",
        explanation: NO_TRANSPORT_EXPLANATION,
        packetsDelta,
        micEnergyDelta,
        micQuiet: false
      };
    }
    if (packetsDelta <= 0) {
      return {
        ok: false,
        reason: "no_outbound_rtp",
        explanation: "Er vertrekt geen RTP van de tablet. De encoder loopt niet of de audio-sender is weg -- de beller hoort niets, en dat ligt op de tablet.",
        packetsDelta,
        micEnergyDelta,
        micQuiet: false
      };
    }
    if (micEnergyDelta !== null && micEnergyDelta <= MIC_SILENT_ENERGY) {
      const osMuted = second.micTrack?.muted === true;
      return {
        ok: false,
        reason: "mic_silent",
        explanation: "RTP vertrekt, maar de microfoon levert digitale stilte (energie nul). Een live microfoon doet dat nooit: de bron staat uit of is gemute" + (osMuted ? " -- het OS meldt de track zelf als muted." : ".") + " Denk aan de microfoonschakelaar van Android (privacy), de app-machtiging of een gekoppeld Bluetooth-apparaat dat de opnameroute overneemt.",
        packetsDelta,
        micEnergyDelta,
        micQuiet: false
      };
    }
    return {
      ok: true,
      reason: "sending",
      explanation: micEnergyDelta === null ? "RTP vertrekt van de tablet; deze WebView geeft geen microfoon-energie, dus over de inhoud is niets te zeggen." : micQuiet ? "RTP vertrekt met signaal, maar ver onder spraakniveau. Sprak de bewoner in dit venster niet, dan is dit normaal; komt dit bij elk gesprek terug, dan levert de microfoon te weinig." : "RTP vertrekt van de tablet met echt signaal van de microfoon. Hoort de beller t\xF3ch niets, dan ligt het voorbij de tablet: Asterisk of het been naar de beller.",
      packetsDelta,
      micEnergyDelta,
      micQuiet
    };
  }
  /**
   * Get WebRTC stats from a PeerConnection.
   */
  async getCallStats(pc) {
    const stats = {};
    try {
      const report = await pc.getStats();
      report.forEach((stat) => {
        if (stat.type === "candidate-pair" && stat.state === "succeeded") {
          stats.rtt = stat.currentRoundTripTime ? stat.currentRoundTripTime * 1e3 : void 0;
        }
        if (stat.type === "inbound-rtp" && stat.kind === "audio") {
          stats.jitter = stat.jitter ? stat.jitter * 1e3 : void 0;
          stats.packetLoss = stat.packetsLost;
        }
        if (stat.type === "codec" && stat.mimeType?.startsWith("audio/")) {
          stats.codec = stat.mimeType;
        }
      });
    } catch {
    }
    return stats;
  }
};

export { CallManager, CallTones, SipService, WebRTCMedia, getUnlockedAudioContext, resumeSharedAudioContext, suspendSharedAudioContext };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map